import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, LogOut, Mail, Phone, Lock } from "lucide-react";
import { API, formatApiError } from "../lib/store";
import AdminContent from "./AdminContent";

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

const TABS = [
  { id: "enquiries", label: "Enquiries" },
  { id: "fleet", label: "Fleet" },
  { id: "jobs", label: "Jobs / Projects" },
];

export default function AdminPage() {
  const [user, setUser] = useState(undefined); // undefined = checking
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [listError, setListError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("enquiries");

  const signOut = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    setUser(null);
    setRows([]);
  };

  const loadEnquiries = async () => {
    setLoading(true);
    setListError("");
    try {
      const r = await axios.get(`${API}/enquiries`, { withCredentials: true });
      setRows(r.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        try {
          await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
          const r = await axios.get(`${API}/enquiries`, { withCredentials: true });
          setRows(r.data);
        } catch {
          setUser(null);
          setListError("");
        }
      } else {
        setListError(formatApiError(err, "Could not load enquiries."));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const checkSession = async () => {
      try {
        const r = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(r.data);
      } catch {
        try {
          await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
          const r = await axios.get(`${API}/auth/me`, { withCredentials: true });
          setUser(r.data);
        } catch {
          setUser(null);
        }
      }
    };
    checkSession();
  }, []);

  useEffect(() => {
    if (user) loadEnquiries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const r = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(r.data.user);
      setPassword("");
    } catch (err) {
      setError(formatApiError(err, "Could not sign in. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink">
        <Loader2 size={22} className="animate-spin text-brand" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink px-6">
        <form
          data-testid="admin-login-form"
          onSubmit={submit}
          className="w-full max-w-sm border border-line bg-surface p-8"
        >
          <span className="flex h-11 w-11 items-center justify-center bg-brand text-white">
            <Lock size={18} />
          </span>
          <h1 className="mt-6 font-display text-2xl uppercase tracking-tight text-slate-100">
            Admin sign in
          </h1>
          <p className="mt-2 text-sm text-steel">
            Sign in with your email and password to manage enquiries, fleet and
            jobs.
          </p>
          <input
            data-testid="admin-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            autoComplete="username"
            required
            className="mt-5 w-full border border-line bg-transparent px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-colors focus:border-brand"
          />
          <input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            autoComplete="current-password"
            required
            className="mt-3 w-full border border-line bg-transparent px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-colors focus:border-brand"
          />
          {error && (
            <p data-testid="admin-error" className="mt-3 text-xs text-brand">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-button"
            type="submit"
            disabled={loading}
            className="mt-5 flex w-full items-center justify-center gap-2 bg-brand py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-white transition-colors hover:bg-brand-hover disabled:opacity-60"
          >
            {loading && <Loader2 size={14} className="animate-spin" />}
            Sign In
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink px-6 py-10 lg:px-12">
      <div className="mx-auto max-w-4xl">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-steel">
              Udat Marine Sdn Bhd
            </p>
            <h1 className="mt-2 font-display text-3xl uppercase tracking-tight text-slate-100">
              Admin
            </h1>
          </div>
          <button
            data-testid="admin-signout"
            onClick={signOut}
            className="flex items-center gap-2 border border-line px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] text-steel transition-colors hover:border-brand hover:text-brand"
          >
            <LogOut size={13} /> Sign out
          </button>
        </div>

        <div className="mt-8 flex gap-2 border-b border-line">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-5 py-3 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors ${
                tab === t.id
                  ? "border-b-2 border-brand text-white"
                  : "text-steel hover:text-white"
              }`}
            >
              {t.label}
              {t.id === "enquiries" && ` (${rows.length})`}
            </button>
          ))}
        </div>

        {tab === "enquiries" && (
          <div className="mt-8 pb-16">
            {loading && (
              <p className="flex items-center gap-2 text-sm text-steel">
                <Loader2 size={14} className="animate-spin" /> Loading…
              </p>
            )}
            {listError && (
              <p data-testid="admin-list-error" className="text-sm text-brand">
                {listError}
              </p>
            )}
            <div data-testid="admin-enquiries-list" className="space-y-3">
              {rows.map((r) => (
                <div
                  key={r.id}
                  data-testid="enquiry-row"
                  className="border border-line bg-surface p-5"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="font-display text-lg uppercase tracking-tight text-slate-100">
                      {r.name}
                      {r.company && (
                        <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.16em] text-steel">
                          {r.company}
                        </span>
                      )}
                    </div>
                    <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-steel">
                      {fmtDate(r.created_at)}
                    </span>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-steel">
                    <a
                      href={`mailto:${r.email}`}
                      className="flex items-center gap-1.5 transition-colors hover:text-brand"
                    >
                      <Mail size={13} /> {r.email}
                    </a>
                    {r.phone && (
                      <a
                        href={`tel:${r.phone.replace(/\s/g, "")}`}
                        className="flex items-center gap-1.5 transition-colors hover:text-brand"
                      >
                        <Phone size={13} /> {r.phone}
                      </a>
                    )}
                    <span className="border border-line px-2 py-1 font-mono text-[9px] uppercase tracking-[0.14em] text-slate-300">
                      {r.service}
                    </span>
                  </div>
                  <p className="mt-3 whitespace-pre-wrap text-sm font-light leading-relaxed text-slate-300">
                    {r.message}
                  </p>
                </div>
              ))}
              {!loading && rows.length === 0 && !listError && (
                <p className="text-sm text-steel">
                  No enquiries yet — they will appear here as the website form is
                  used.
                </p>
              )}
            </div>
          </div>
        )}

        {tab === "fleet" && (
          <div className="mt-8">
            <AdminContent
              collection="fleet"
              heading="Fleet"
              useSpecs
              onSessionExpired={signOut}
            />
          </div>
        )}

        {tab === "jobs" && (
          <div className="mt-8">
            <AdminContent
              collection="projects"
              heading="Jobs / Projects"
              onSessionExpired={signOut}
            />
          </div>
        )}
      </div>
    </div>
  );
}
